import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tablet, RefreshCcw, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import { houseService } from "@/services/houseService";
import { deviceKeys, deviceService } from "@/services/deviceService";
import {
  Button,
  ConfirmModal,
  DefinitionList,
  DetailRow,
  Modal,
  ModalFooter,
  SearchField,
  EmptyState,
} from "@/components/ui";
import { PickerPanel } from "@/components/common";
import { DevicePosConfigModal } from "@/components/device/DevicePosConfigModal";
import { DeviceCredentialsModal } from "@/components/device/DeviceCredentialsModal";
import { DeviceTelemetryPanel } from "@/components/device/DeviceTelemetryPanel";
import { ClickToCopyId } from "@/components/common";
import {
  DevicesTable,
  type DeviceTableSortColumn,
} from "@/components/device/DevicesTable";
import { DataTableTitle, queryLoadState } from "@/components/table";
import { formatDeviceAppVersion } from "@/lib/deviceStatus";
import { toastApiError } from "@/lib/errorMessages";
import { matchesTextQuery } from "@/lib/textSearch";
import { isReaderPaired } from "@/lib/readerLabel";
import type { Device, HouseDevice } from "@/types";
import { useHouseDevicesReorder } from "./useHouseDevicesReorder";
import { DevicePresencePanel } from "./DevicePresencePanel";
import { presenceGuestDisplayName, formatPresenceCalendarDate } from "@/lib/presenceDisplay";

interface HouseAdminDevicesSectionProps {
  houseId: string;
  houseName: string;
}

/** Global device update responses omit house-scoped presence fields — keep cache values. */
function mergeDeviceKeepingPresence(
  existing: HouseDevice,
  updated: Device,
): HouseDevice {
  return {
    ...existing,
    ...updated,
    region_code: existing.region_code,
    current_presence:
      updated.current_presence !== undefined
        ? updated.current_presence
        : existing.current_presence,
    upcoming_presence_count:
      updated.upcoming_presence_count !== undefined
        ? updated.upcoming_presence_count
        : existing.upcoming_presence_count,
  };
}

function toHouseDevice(device: Device, regionCode: HouseDevice["region_code"]): HouseDevice {
  return {
    ...device,
    region_code: regionCode,
  };
}

/** Diagnostic column kept out of the default house view so row actions fit. */
const HOUSE_HIDDEN_DEVICE_COLUMNS: readonly DeviceTableSortColumn[] = [
  "appVersion",
];

export function HouseAdminDevicesSection({
  houseId,
  houseName,
}: HouseAdminDevicesSectionProps) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const { commitOrder, isReordering } = useHouseDevicesReorder(houseId);

  const [isAssigningTablet, setIsAssigningTablet] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [listSearchQuery, setListSearchQuery] = useState("");
  const [otherInfoDevice, setOtherInfoDevice] = useState<Device | null>(null);
  const [presencePanelDevice, setPresencePanelDevice] =
    useState<HouseDevice | null>(null);
  const [posConfigKiosk, setPosConfigKiosk] = useState<Device | null>(null);
  const [credentialModalState, setCredentialModalState] = useState<{
    kioskCode: string;
    password: string;
    title: string;
    description: string;
  } | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    kind: "regenerate" | "unpair" | "unassign";
    device: Device;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const {
    data: devices = [],
    refetch: refetchDevices,
    isFetching: isRefreshingDevices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useQuery({
    queryKey: deviceKeys.byHouse(houseId),
    queryFn: () => houseService.listDevices(houseId),
    enabled: !!houseId,
  });
  const devicesLoadState = queryLoadState({
    isLoading: isLoadingDevices,
    error: devicesError,
    errorMessage: t("loadDevices", { ns: "errors" }),
    retryLabel: tCommon("tryAgain"),
    onRetry: () => void refetchDevices(),
  });

  const {
    data: allDevices = [],
    isError: allDevicesError,
    error: allDevicesQueryError,
  } = useQuery({
    queryKey: deviceKeys.list(),
    queryFn: () => deviceService.getAll(),
    // Only needed for the assign-tablet picker — avoid toasting on every house visit.
    enabled: isAssigningTablet,
  });

  useEffect(() => {
    if (isAssigningTablet && allDevicesError) {
      toastApiError(allDevicesQueryError, "loadDevices");
    }
  }, [isAssigningTablet, allDevicesError, allDevicesQueryError]);

  const patchAllDevicesCache = (updater: (current: Device[]) => Device[]) => {
    queryClient.setQueryData<Device[]>(
      deviceKeys.list(),
      (current = []) => updater(current),
    );
  };

  const invalidateDeviceLists = () =>
    queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });

  // Keep the "other info" modal on the latest row after a reload.
  useEffect(() => {
    if (!otherInfoDevice) return;
    const refreshed = devices.find((device) => device.id === otherInfoDevice.id);
    if (!refreshed) {
      setOtherInfoDevice(null);
      return;
    }
    if (refreshed !== otherInfoDevice) {
      setOtherInfoDevice(refreshed);
    }
  }, [devices, otherInfoDevice]);

  useEffect(() => {
    if (!presencePanelDevice) return;
    const refreshed = devices.find(
      (device) => device.id === presencePanelDevice.id,
    );
    if (!refreshed) {
      setPresencePanelDevice(null);
      return;
    }
    if (refreshed !== presencePanelDevice) {
      setPresencePanelDevice(refreshed);
    }
  }, [devices, presencePanelDevice]);

  const assignDeviceMutation = useMutation({
    mutationFn: (deviceId: string) =>
      deviceService.assignToHouse(deviceId, houseId),
    onSuccess: async (assignedDevice) => {
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) => {
          const withoutDevice = current.filter(
            (device) => device.id !== assignedDevice.id,
          );
          const regionCode =
            assignedDevice.region_code ?? current[0]?.region_code;
          // Append even when region_code is still unknown: the invalidation
          // below refetches the canonical row.
          return [
            ...withoutDevice,
            regionCode != null
              ? toHouseDevice(assignedDevice, regionCode)
              : (assignedDevice as HouseDevice),
          ];
        },
      );
      patchAllDevicesCache((current) =>
        current.map((device) =>
          device.id === assignedDevice.id ? assignedDevice : device,
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(houseId),
      });
      await queryClient.invalidateQueries({ queryKey: ["house", houseId] });
      await invalidateDeviceLists();
      toast.success(t("adminDetail.toastAssignSuccess"));
      setIsAssigningTablet(false);
      setSelectedDeviceId("");
      setDeviceSearch("");
    },
    onError: (err) => {
      toastApiError(err, "assignDevice");
    },
  });

  const filteredDevices = useMemo(() => {
    if (!listSearchQuery.trim()) return devices;
    return devices.filter((device) =>
      matchesTextQuery(listSearchQuery, [
        device.display_name,
        device.kiosk_code,
        device.serial_number,
        device.google_account,
        device.software_id,
        device.reader?.serial_number,
      ]),
    );
  }, [devices, listSearchQuery]);

  const availableDevices = allDevices.filter((d) => {
    const isUnassigned = !d.house_id;
    const matchesSearch =
      !deviceSearch ||
      matchesTextQuery(deviceSearch, [
        d.display_name,
        d.kiosk_code,
        d.serial_number,
        d.software_id,
      ], { trim: false });
    return isUnassigned && matchesSearch;
  });

  const closeAssignModal = () => {
    setIsAssigningTablet(false);
    setDeviceSearch("");
    setSelectedDeviceId("");
  };

  const handleRegenerateCredentials = (device: Device) => {
    setPendingAction({ kind: "regenerate", device });
  };

  const handleToggleActive = async (device: Device) => {
    const previousRevoked = device.is_revoked === true;
    const nextRevoked = !previousRevoked;
    queryClient.setQueryData<HouseDevice[]>(
      deviceKeys.byHouse(houseId),
      (current = []) =>
        current.map((item) =>
          item.id === device.id ? { ...item, is_revoked: nextRevoked } : item,
        ),
    );
    patchAllDevicesCache((current) =>
      current.map((item) =>
        item.id === device.id ? { ...item, is_revoked: nextRevoked } : item,
      ),
    );
    queryClient.setQueriesData<Device[]>(
      { queryKey: deviceKeys.lists() },
      (current) =>
        current?.map((item) =>
          item.id === device.id ? { ...item, is_revoked: nextRevoked } : item,
        ),
    );
    try {
      await deviceService.update(device.id, { is_revoked: nextRevoked });
      toast.success(
        previousRevoked
          ? t("adminDetail.toastDeviceReactivated")
          : t("adminDetail.toastDeviceRevoked"),
      );
      void queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(houseId),
      });
      void invalidateDeviceLists();
    } catch (err) {
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === device.id
              ? { ...item, is_revoked: previousRevoked }
              : item,
          ),
      );
      patchAllDevicesCache((current) =>
        current.map((item) =>
          item.id === device.id
            ? { ...item, is_revoked: previousRevoked }
            : item,
        ),
      );
      queryClient.setQueriesData<Device[]>(
        { queryKey: deviceKeys.lists() },
        (current) =>
          current?.map((item) =>
            item.id === device.id
              ? { ...item, is_revoked: previousRevoked }
              : item,
          ),
      );
      toastApiError(err, "updateDevice");
    }
  };

  const handleToggleShelved = async (device: Device) => {
    const previousShelved = device.is_shelved === true;
    const nextShelved = !previousShelved;
    queryClient.setQueryData<HouseDevice[]>(
      deviceKeys.byHouse(houseId),
      (current = []) =>
        current.map((item) =>
          item.id === device.id ? { ...item, is_shelved: nextShelved } : item,
        ),
    );
    patchAllDevicesCache((current) =>
      current.map((item) =>
        item.id === device.id ? { ...item, is_shelved: nextShelved } : item,
      ),
    );
    queryClient.setQueriesData<Device[]>(
      { queryKey: deviceKeys.lists() },
      (current) =>
        current?.map((item) =>
          item.id === device.id ? { ...item, is_shelved: nextShelved } : item,
        ),
    );
    try {
      await deviceService.update(device.id, { is_shelved: nextShelved });
      toast.success(
        previousShelved
          ? t("adminDetail.toastDeviceUnshelved")
          : t("adminDetail.toastDeviceShelved"),
      );
      void queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(houseId),
      });
      void invalidateDeviceLists();
    } catch (err) {
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === device.id
              ? { ...item, is_shelved: previousShelved }
              : item,
          ),
      );
      patchAllDevicesCache((current) =>
        current.map((item) =>
          item.id === device.id
            ? { ...item, is_shelved: previousShelved }
            : item,
        ),
      );
      queryClient.setQueriesData<Device[]>(
        { queryKey: deviceKeys.lists() },
        (current) =>
          current?.map((item) =>
            item.id === device.id
              ? { ...item, is_shelved: previousShelved }
              : item,
          ),
      );
      toastApiError(err, "updateDevice");
    }
  };

  const handleUnassignDevice = (device: Device) => {
    setPendingAction({ kind: "unassign", device });
  };

  const handleUpdateDisplayName = async (
    device: Device,
    displayName: string,
  ) => {
    try {
      const updatedDevice = await deviceService.update(device.id, {
        display_name: displayName,
      });
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === updatedDevice.id
              ? mergeDeviceKeepingPresence(item, updatedDevice)
              : item,
          ),
      );
      patchAllDevicesCache((current) =>
        current.map((item) =>
          item.id === updatedDevice.id
            ? { ...item, ...updatedDevice }
            : item,
        ),
      );
      await invalidateDeviceLists();
      toast.success(t("adminDetail.toastDeviceRenamed"));
    } catch (err) {
      toastApiError(err, "renameDevice");
      throw err;
    }
  };

  const handleUpdateGoogleAccount = async (
    device: Device,
    googleAccount: string,
  ) => {
    try {
      const updatedDevice = await deviceService.update(device.id, {
        google_account: googleAccount,
      });
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === updatedDevice.id
              ? mergeDeviceKeepingPresence(item, updatedDevice)
              : item,
          ),
      );
      patchAllDevicesCache((current) =>
        current.map((item) =>
          item.id === updatedDevice.id
            ? { ...item, ...updatedDevice }
            : item,
        ),
      );
      await invalidateDeviceLists();
      toast.success(t("adminDetail.toastGoogleAccountUpdated"));
    } catch (err) {
      toastApiError(err, "updateDevice");
      throw err;
    }
  };

  const handleUpdateNotes = async (device: Device, notes: string | null) => {
    try {
      const updatedDevice = await deviceService.update(device.id, { notes });
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === updatedDevice.id
              ? mergeDeviceKeepingPresence(item, updatedDevice)
              : item,
          ),
      );
      patchAllDevicesCache((current) =>
        current.map((item) =>
          item.id === updatedDevice.id
            ? { ...item, ...updatedDevice }
            : item,
        ),
      );
      await invalidateDeviceLists();
      toast.success(t("adminDetail.toastNotesUpdated"));
    } catch (err) {
      toastApiError(err, "updateDevice");
      throw err;
    }
  };

  const handleUnlinkReader = (device: Device) => {
    if (!isReaderPaired(device.reader)) return;
    setPendingAction({ kind: "unpair", device });
  };

  const handleConfirmPendingAction = async () => {
    if (!pendingAction) return;
    const { kind, device } = pendingAction;
    setActionBusy(true);
    try {
      if (kind === "regenerate") {
        const response = await houseService.resetDevicePassword(
          houseId,
          device.id,
        );
        setCredentialModalState({
          kioskCode: device.kiosk_code,
          password: response.password,
          title: t("detail.credentialModalTitle"),
          description: t("detail.credentialModalDesc"),
        });
        await refetchDevices();
        toast.success(t("credentialsRegenerated"));
      } else if (kind === "unpair") {
        await deviceService.unlinkReader(device.id, houseId);
        await refetchDevices();
        await queryClient.invalidateQueries({
          queryKey: deviceKeys.byHouse(houseId),
        });
        await invalidateDeviceLists();
        toast.success(t("adminDetail.readerUnpaired"));
      } else {
        const updatedDevice = await deviceService.unassignFromHouse(device.id);
        queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
          (current = []) =>
            current.filter((item) => item.id !== updatedDevice.id),
        );
        patchAllDevicesCache((current) =>
          current.map((item) =>
            item.id === updatedDevice.id ? updatedDevice : item,
          ),
        );
        await invalidateDeviceLists();
        toast.success(t("adminDetail.toastDeviceUnassigned"));
      }
      setPendingAction(null);
    } catch (err) {
      toastApiError(
        err,
        kind === "regenerate"
          ? "regenerateCredentials"
          : kind === "unpair"
            ? "unlinkReader"
            : "unassignDevice",
      );
    } finally {
      setActionBusy(false);
    }
  };

  const tableLabels = {
    colDevice: t("adminDetail.colDevice"),
    colSerial: t("adminDetail.colSerial"),
    colGoogleAccount: t("adminDetail.colGoogleAccount"),
    colFreekioskUnlock: t("adminDetail.colFreekioskUnlock"),
    colReader: t("adminDetail.colReader"),
    colLastTelemetry: t("adminDetail.colLastTelemetry"),
    colLastInteraction: t("adminDetail.colLastInteraction"),
    colAppVersion: t("adminDetail.colAppVersion"),
    colInUse: t("adminDetail.colInUse"),
    colActions: t("adminDetail.colActions"),
    renameDeviceTitle: t("adminDetail.renameDeviceTitle"),
    displayNamePlaceholder: t("adminDetail.displayNamePlaceholder"),
    displayNameEmpty: t("adminDetail.displayNameEmpty"),
    editGoogleAccountTitle: t("adminDetail.editGoogleAccountTitle"),
    editNotesTitle: t("adminDetail.editNotesTitle"),
    notesPlaceholder: t("adminDetail.notesPlaceholder"),
    emptyDefault: t("detail.noDevices"),
    readerPair: t("adminDetail.readerPair"),
    readerUnpairTitle: t("adminDetail.readerUnpairTitle"),
    lastTelemetryNever: t("adminDetail.valueTelemetryNever"),
    valueAppVersionUnknown: t("adminDetail.valueAppVersionUnknown"),
    valueAppVersionLegacy: t("adminDetail.valueAppVersionLegacy"),
    valueFreekioskUnlockUnavailable: t(
      "adminDetail.valueFreekioskUnlockUnavailable",
    ),
    moreActionsTitle: t("adminDetail.moreActionsTitle"),
    menuRegenerateCredentials: t("adminDetail.menuRegenerateCredentials"),
    menuRevokeDevice: t("adminDetail.menuRevokeDevice"),
    menuReactivateDevice: t("adminDetail.menuReactivateDevice"),
    menuManagePresences: t("adminDetail.menuManagePresences"),
    menuNotes: t("adminDetail.menuNotes"),
    menuOtherInfo: t("adminDetail.menuOtherInfo"),
    unassignDeviceTitle: t("adminDetail.unassignDeviceTitle"),
    telemetryBattery: t("adminDetail.telemetryBattery"),
    telemetryWifi: t("adminDetail.telemetryWifi"),
    telemetryAudio: t("adminDetail.telemetryAudio"),
    telemetryStorage: t("adminDetail.telemetryStorage"),
    telemetryMemory: t("adminDetail.telemetryMemory"),
    telemetryUnavailable: t("adminDetail.telemetryUnavailable"),
    telemetryDataAge: (age: string) =>
      t("adminDetail.telemetryDataAge", { age }),
  };

  return (
    <>
      <section>
        {devices.length === 0 && !devicesLoadState ? (
          <EmptyState
            variant="dashed"
            size="md"
            icon={Tablet}
            title={t("detail.noDevices")}
            description={t("detail.tabs.devicesHintAdmin")}
            action={
              <Button
                variant="outline"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setIsAssigningTablet(true)}
              >
                {t("adminDetail.assignDeviceCta")}
              </Button>
            }
          />
        ) : (
          <DevicesTable
            title={
              <DataTableTitle
                icon={Tablet}
                count={devicesLoadState ? undefined : devices.length}
                description={t("detail.tabs.devicesHintAdmin")}
              >
                {t("detail.tabs.devices")}
              </DataTableTitle>
            }
            actions={
              <>
                <SearchField
                  searchLabel={tCommon("search")}
                  clearSearchLabel={tCommon("clearSearch")}
                  value={listSearchQuery}
                  onChange={setListSearchQuery}
                  placeholder={t("adminDetail.filterDevicesPlaceholder")}
                  className="w-full sm:w-72"
                  inputClassName="h-8"
                />
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => void refetchDevices()}
                  isLoading={isRefreshingDevices}
                  leftIcon={<RefreshCcw className="h-4 w-4" />}
                  aria-label={tCommon("refresh")}
                />
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsAssigningTablet(true)}
                >
                  {t("adminDetail.assignDevice")}
                </Button>
              </>
            }
            devices={filteredDevices}
            loadState={devicesLoadState}
            labels={tableLabels}
            preferencesKey="house-devices"
            defaultHiddenColumns={HOUSE_HIDDEN_DEVICE_COLUMNS}
            resetToken={listSearchQuery}
            emptyMessage={
              listSearchQuery.trim()
                ? t("adminDetail.assignModalNoResultsSearch", {
                    query: listSearchQuery.trim(),
                  })
                : undefined
            }
            onReorder={commitOrder}
            reorderDisabled={isReordering || listSearchQuery.trim().length > 0}
            dragHandleLabel={t("detail.dragToReorder")}
            onPairReader={setPosConfigKiosk}
            onUnpairReader={(device) => void handleUnlinkReader(device)}
            onUnassignDevice={(device) => void handleUnassignDevice(device)}
            onRegenerateCredentials={(device) =>
              void handleRegenerateCredentials(device)
            }
            onToggleActive={(device) => void handleToggleActive(device)}
            onToggleShelved={(device) => void handleToggleShelved(device)}
            onShowOtherInfo={setOtherInfoDevice}
            onManagePresences={(device) =>
              setPresencePanelDevice(device as HouseDevice)
            }
            onUpdateDisplayName={handleUpdateDisplayName}
            onUpdateGoogleAccount={handleUpdateGoogleAccount}
            onUpdateNotes={handleUpdateNotes}
          />
        )}
      </section>

      <DevicePosConfigModal
        isOpen={!!posConfigKiosk}
        device={posConfigKiosk}
        houseId={houseId}
        onClose={() => setPosConfigKiosk(null)}
        onUpdated={async () => {
          await queryClient.invalidateQueries({
            queryKey: deviceKeys.byHouse(houseId),
          });
          await invalidateDeviceLists();
          toast.success(t("adminDetail.toastReaderUpdated"));
        }}
      />

      <DeviceCredentialsModal
        isOpen={credentialModalState !== null}
        title={credentialModalState?.title ?? ""}
        description={credentialModalState?.description ?? ""}
        kioskCode={credentialModalState?.kioskCode ?? ""}
        password={credentialModalState?.password ?? ""}
        onClose={() => setCredentialModalState(null)}
      />

      <Modal
        closeLabel={tCommon("close")}
        isOpen={isAssigningTablet}
        onClose={closeAssignModal}
        title={t("adminDetail.assignModalTitle")}
        size="sm"
        footer={
          <ModalFooter>
            <Button variant="outline" onClick={closeAssignModal}>
              {tCommon("cancel")}
            </Button>
            <Button
              onClick={() => assignDeviceMutation.mutate(selectedDeviceId)}
              disabled={!selectedDeviceId || assignDeviceMutation.isPending}
              isLoading={assignDeviceMutation.isPending}
            >
              {t("adminDetail.assign")}
            </Button>
          </ModalFooter>
        }
      >
            <p className="mb-4 text-[13px] text-surface-500">
              {t("adminDetail.assignModalIntro", { houseName })}
            </p>
            <div className="space-y-4">
              <PickerPanel
                stepLabel={t("adminDetail.searchDevicesLabel")}
                searchPlaceholder={t("adminDetail.searchDevicesPlaceholder")}
                searchValue={deviceSearch}
                onSearchChange={setDeviceSearch}
                isLoading={false}
                listClassName="max-h-64"
                emptyMessage={
                  deviceSearch
                    ? t("adminDetail.assignModalNoResultsSearch", {
                        query: deviceSearch,
                      })
                    : t("adminDetail.assignModalNoResults")
                }
                items={availableDevices.map((d) => ({
                  id: d.id,
                  title: d.display_name,
                  subtitle: t("adminDetail.deviceListMeta", {
                    code: d.kiosk_code,
                    serial: d.serial_number,
                  }),
                  selected: selectedDeviceId === d.id,
                  onSelect: () => setSelectedDeviceId(d.id),
                }))}
              />
            </div>
      </Modal>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={otherInfoDevice != null}
        onClose={() => setOtherInfoDevice(null)}
        title={t("adminDetail.otherInfoTitle")}
        size="lg"
        footer={
          <ModalFooter>
            <Button onClick={() => setOtherInfoDevice(null)}>
              {tCommon("close")}
            </Button>
          </ModalFooter>
        }
      >
        {otherInfoDevice ? (
          <>
            <DefinitionList className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <DetailRow label={t("adminDetail.fieldDisplayName")} valueClassName="font-medium">
                {otherInfoDevice.display_name}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldKioskCode")} valueClassName="font-mono">
                {otherInfoDevice.kiosk_code}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldSerialNumber")}>
                <ClickToCopyId
                  value={otherInfoDevice.serial_number}
                  ellipsis="start"
                />
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldGoogleAccount")} valueClassName="break-all">
                {otherInfoDevice.google_account}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldSoftwareId")} valueClassName="break-all font-mono text-[13px]">
                {otherInfoDevice.software_id || t("adminDetail.valueUnbound")}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldReaderId")} valueClassName="break-all font-mono text-[13px]">
                {otherInfoDevice.reader?.serial_number ||
                  t("adminDetail.valueNotPaired")}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldBrand")}>
                {otherInfoDevice.brand || "-"}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldDisplayInches")} valueClassName="tabular-nums">
                {otherInfoDevice.display_inches != null
                  ? `${otherInfoDevice.display_inches}"`
                  : "-"}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldResolution")} valueClassName="tabular-nums">
                {otherInfoDevice.display_resolution || "-"}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldAndroidVersion")} valueClassName="tabular-nums">
                {otherInfoDevice.android_version || "-"}
              </DetailRow>
              <DetailRow label={t("adminDetail.fieldAppVersion")} valueClassName="font-mono text-[13px]">
                {formatDeviceAppVersion(otherInfoDevice, {
                  unknown: t("adminDetail.valueAppVersionUnknown"),
                  legacy: t("adminDetail.valueAppVersionLegacy"),
                })}
              </DetailRow>
              <DetailRow label={t("detail.currentGuest")} className="sm:col-span-2">
                {otherInfoDevice.current_presence ? (
                  <span className="font-medium text-success-700">
                    {presenceGuestDisplayName(
                      otherInfoDevice.current_presence.guest_name,
                      t("detail.presences.genericGuestName"),
                    )}
                    <span className="font-normal text-surface-500">
                      {" · "}
                      {t("detail.guestDeparts", {
                        date: formatPresenceCalendarDate(
                          otherInfoDevice.current_presence.departure_date,
                        ),
                      })}
                    </span>
                    {(otherInfoDevice.upcoming_presence_count ?? 0) > 0
                      ? ` · ${t("detail.queuedGuests", {
                          count: otherInfoDevice.upcoming_presence_count ?? 0,
                        })}`
                      : ""}
                  </span>
                ) : (otherInfoDevice.upcoming_presence_count ?? 0) > 0 ? (
                  <span className="font-medium text-surface-700">
                    {t("detail.noCurrentGuestWithQueue", {
                      count: otherInfoDevice.upcoming_presence_count ?? 0,
                    })}
                  </span>
                ) : (
                  <span className="text-surface-500">
                    {t("detail.noGuestPresence")}
                  </span>
                )}
              </DetailRow>
            </DefinitionList>
              <DeviceTelemetryPanel
                device={otherInfoDevice}
                labels={{
                  title: t("adminDetail.sectionTelemetryData"),
                  lastReport: t("adminDetail.fieldLastTelemetryReport"),
                  sectionDeviceStatus: t(
                    "adminDetail.sectionTelemetryDeviceStatus",
                  ),
                  sectionGpsLocation: t(
                    "adminDetail.sectionTelemetryGpsLocation",
                  ),
                  neverReceived: t("adminDetail.valueTelemetryNever"),
                  emptyPayload: t("adminDetail.valueTelemetryEmpty"),
                }}
              />
          </>
        ) : null}
      </Modal>

      {presencePanelDevice ? (
        <DevicePresencePanel
          isOpen
          onClose={() => setPresencePanelDevice(null)}
          houseId={houseId}
          kiosk={presencePanelDevice}
        />
      ) : null}

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={pendingAction != null}
        onClose={() => {
          if (!actionBusy) setPendingAction(null);
        }}
        onConfirm={() => void handleConfirmPendingAction()}
        title={
          pendingAction?.kind === "regenerate"
            ? t("adminDetail.menuRegenerateCredentials")
            : pendingAction?.kind === "unpair"
              ? t("adminDetail.readerUnpairTitle")
              : t("adminDetail.unassignDeviceTitle")
        }
        message={
          pendingAction?.kind === "regenerate"
            ? t("detail.regenerateConfirm", {
                name: pendingAction.device.display_name,
              })
            : pendingAction?.kind === "unpair"
              ? t("adminDetail.readerUnpairConfirm")
              : t("adminDetail.unassignConfirm", {
                  name: pendingAction?.device.display_name ?? "",
                })
        }
        isDestructive={pendingAction?.kind !== "regenerate"}
        isLoading={actionBusy}
      />
    </>
  );
}
